// src/services/fishIQ.ts
// FishIQ data access layer (MVP: normalized seed + optional Firestore overlay).
// Public callers stay stable while internals evolve.

import type {
  FishHabitatType,
  FishIQRegion,
  FishIQSpecies,
  FishIQSpeciesSeed,
} from '../types';
import { FISHIQ_SPECIES_SEED } from '../constants/fishIQSeed';
import { FEATURES } from '../constants/features';
import { fishIQSpeciesAllowedForWaterContext } from './fishIQWaterContext';

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

let cachedSpecies: FishIQSpecies[] | null = null;
let cachedRegions: FishIQRegion[] | null = null;

const normalize = (s: any) => String(s ?? '').trim().toLowerCase();

const normalizeSearchText = (value: any) =>
  normalize(value)
    .replace(/&/g, ' and ')
    .replace(/[_\-]+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const searchValueText = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(searchValueText).filter(Boolean).join(' ');
  if (typeof value === 'object') {
    return [value?.name, value?.title, value?.label, value?.id, value?.key]
      .map(searchValueText)
      .filter(Boolean)
      .join(' ');
  }
  return String(value);
};

export const fishIQSpeciesMatchesSearch = (item: any, term: string): boolean => {
  const q = normalizeSearchText(term);
  if (!q) return true;

  const canonical = item?.canonicalSpecies || {};
  const fields = [
    item?.id,
    item?.slug,
    item?.commonName,
    item?.name,
    item?.scientificName,
    item?.family,
    item?.category,
    item?.aquaticKind,
    item?.summary,
    item?.caution,
    item?.aliases,
    item?.habitats,
    item?.regions,
    item?.waterRealms,
    item?.harvestModes,
    item?.behaviorTags,
    item?.structureTags,
    item?.keyTraits,
    item?.bestBaits,
    item?.bestTechniques,
    item?.bestSeasons,
    item?.bestTimesOfDay,
    item?.feedingProfile?.primaryPrey,
    item?.feedingProfile?.prey,
    canonical?.canonicalId,
    canonical?.legacyIds,
    canonical?.normalizedScientificName,
    canonical?.normalizedCommonName,
    canonical?.aliases,
    canonical?.habitatZones,
    canonical?.waterRealms,
    canonical?.regionalCoverage,
  ];

  const searchable = normalizeSearchText(fields.map(searchValueText).filter(Boolean).join(' '));
  if (!searchable) return false;
  if (searchable.includes(q)) return true;

  const tokens = q.split(' ').filter(Boolean);
  return tokens.length > 1 && tokens.every((token) => searchable.includes(token));
};

type ConfidenceTier = 'general' | 'limited' | 'local';

type FishIQMeta = {
  source: 'seed' | 'firestore' | 'hybrid';
  observationCount: number;
  confidenceTier: ConfidenceTier;
  confidenceLabel: string;
};

export type WaterTypeBias =
  | 'lake'
  | 'river'
  | 'reservoir'
  | 'pond'
  | 'creek'
  | 'inshore'
  | 'estuary'
  | 'bay'
  | 'ocean'
  | 'canal'
  | 'marsh'
  | 'other'
  | 'stream'
  | 'coast'
  | 'unknown'
  | string
  | null
  | undefined;

const VALID_HABITATS: FishHabitatType[] = [
  'lake',
  'river',
  'reservoir',
  'pond',
  'creek',
  'inshore',
  'estuary',
  'bay',
  'ocean',
  'canal',
  'marsh',
  'reef',
  'backwater',
  'other',
];

export const normalizeHabitat = (value: any): FishHabitatType | null => {
  const raw = normalize(value);
  if (!raw) return null;
  if ((VALID_HABITATS as string[]).includes(raw)) return raw as FishHabitatType;
  if (raw.includes('backwater')) return 'backwater';
  if (raw.includes('reef') || raw.includes('coral')) return 'reef';
  if (raw.includes('shore') || raw.includes('coast')) return 'inshore';
  if (raw.includes('estuary')) return 'estuary';
  if (raw.includes('bay')) return 'bay';
  if (raw.includes('ocean') || raw.includes('offshore') || raw.includes('sea')) return 'ocean';
  if (raw.includes('canal')) return 'canal';
  if (raw.includes('marsh') || raw.includes('wetland')) return 'marsh';
  if (raw.includes('creek') || raw.includes('stream')) return 'creek';
  if (raw.includes('pond')) return 'pond';
  if (raw.includes('reservoir')) return 'reservoir';
  if (raw.includes('river')) return 'river';
  if (raw.includes('lake')) return 'lake';
  return 'other';
};

export const normalizeWaterTypeBias = (value: WaterTypeBias): FishHabitatType | null => {
  const raw = normalize(value);
  if (!raw || raw === 'unknown') return null;
  if (raw === 'stream') return 'creek';
  if (raw === 'coast' || raw === 'coastline' || raw === 'shoreline') return 'inshore';
  return normalizeHabitat(raw);
};

const SALTWATER_BIASES: FishHabitatType[] = ['inshore', 'estuary', 'bay', 'ocean', 'marsh', 'reef', 'backwater'];
const FRESHWATER_BIASES: FishHabitatType[] = ['lake', 'river', 'reservoir', 'pond', 'creek', 'canal'];

const isSaltwaterBias = (bias: FishHabitatType | null) => !!bias && SALTWATER_BIASES.includes(bias);
const isFreshwaterBias = (bias: FishHabitatType | null) => !!bias && FRESHWATER_BIASES.includes(bias);

const speciesMatchesWaterTruth = (item: any, bias: FishHabitatType | null): boolean => {
  if (!bias) return true;

  // Shared launch guardrail: category alone is too weak for U.S. regional packs.
  // Some species are marked `both` because they are anadromous/brackish, but their
  // actual habitat list may still be coastal-only for the current read. Reuse the
  // same water-context truth helper as the map ranker/hotspot picker so fallback
  // catalog diversity cannot reintroduce marine-only fish inland.
  const allowedByWaterContext = fishIQSpeciesAllowedForWaterContext(item, {
    waterKind: bias,
    waterType: bias,
  });
  if (!allowedByWaterContext) return false;

  const category = normalize(item?.category);
  if (category === 'both') return true;
  if (isFreshwaterBias(bias) && category === 'saltwater') return false;
  if (isSaltwaterBias(bias) && category === 'freshwater') return false;

  const habitats = Array.isArray(item?.habitats) ? item.habitats.map(normalizeHabitat).filter(Boolean) as FishHabitatType[] : [];
  if (!habitats.length) return true;

  const hasSaltHabitat = habitats.some((h) => SALTWATER_BIASES.includes(h));
  const hasFreshHabitat = habitats.some((h) => FRESHWATER_BIASES.includes(h));
  if (isFreshwaterBias(bias) && hasSaltHabitat && !hasFreshHabitat) return false;
  if (isSaltwaterBias(bias) && hasFreshHabitat && !hasSaltHabitat) return false;
  return true;
};

const uniqueStrings = (values: any[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values || []) {
    const label = String(value ?? '').trim();
    if (!label) continue;
    const key = normalize(label);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
};

const normalizeStringArray = (value: any, limit = 24): string[] => {
  if (Array.isArray(value)) return uniqueStrings(value).slice(0, limit);
  if (typeof value === 'string') return uniqueStrings(value.split(/\n|,|•|·/g)).slice(0, limit);
  return [];
};

const normalizeDepthPreference = (value: any): any => {
  if (!value) return undefined;
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'object') {
    const out: any = {};
    for (const key of ['shallow', 'mid', 'deep', 'notes']) {
      const text = String(value?.[key] ?? '').trim();
      if (text) out[key] = text;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
};

const normalizeImageMeta = (raw: any, fallback?: any) => ({
  imageUrl: String(raw?.imageUrl ?? fallback?.imageUrl ?? '').trim() || undefined,
  imageCredit: String(raw?.imageCredit ?? fallback?.imageCredit ?? '').trim() || undefined,
  imageLicense: String(raw?.imageLicense ?? fallback?.imageLicense ?? '').trim() || undefined,
  wikiTitle: String(raw?.wikiTitle ?? fallback?.wikiTitle ?? '').trim() || undefined,
  wikidataId: String(raw?.wikidataId ?? fallback?.wikidataId ?? '').trim() || undefined,
});

const dedupeHabitats = (values: any[]): FishHabitatType[] => {
  const seen = new Set<FishHabitatType>();
  const out: FishHabitatType[] = [];
  for (const value of values || []) {
    const normalized = normalizeHabitat(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
};

const slugFromId = (id: string) =>
  String(id || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const titleCaseWords = (value: string) => value.replace(/\b\w/g, (m) => m.toUpperCase());

const getObservationCount = (raw: any): number => {
  const v = raw?.observationCount ?? raw?.observationsTotal ?? raw?.totalObservations ?? raw?.sampleSize ?? raw?.n ?? 0;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

const confidenceFromCount = (count: number): Pick<FishIQMeta, 'confidenceTier' | 'confidenceLabel'> => {
  if (count <= 0) return { confidenceTier: 'general', confidenceLabel: 'General knowledge' };
  if (count < 5) return { confidenceTier: 'limited', confidenceLabel: 'Limited local data' };
  return { confidenceTier: 'local', confidenceLabel: 'Based on local catches' };
};

const attachMeta = (species: FishIQSpecies, opts: { source: FishIQMeta['source']; observationCount: number }): FishIQSpecies => {
  const { confidenceTier, confidenceLabel } = confidenceFromCount(opts.observationCount);
  return {
    ...(species as any),
    __fishiq: {
      source: opts.source,
      observationCount: opts.observationCount,
      confidenceTier,
      confidenceLabel,
    } as FishIQMeta,
  } as FishIQSpecies;
};

const toSpecies = (s: FishIQSpeciesSeed): FishIQSpecies => {
  const now = Date.now();
  const habitats = dedupeHabitats(s.habitats || []);
  const regions = uniqueStrings(s.regions || []);
  const aliases = uniqueStrings(s.aliases || []);
  return {
    id: String(s.id),
    slug: s.slug || slugFromId(String(s.id)),
    commonName: String(s.commonName || '').trim(),
    scientificName: s.scientificName ? String(s.scientificName).trim() : undefined,
    aliases,
    family: s.family ? String(s.family).trim() : undefined,
    category: s.category,
    aquaticKind: (s as any).aquaticKind || 'finfish',
    waterRealms: normalizeStringArray((s as any).waterRealms, 6) as any,
    harvestModes: normalizeStringArray((s as any).harvestModes, 8) as any,
    edibleCommonly: Boolean((s as any).edibleCommonly),
    fishiqEligible: (s as any).fishiqEligible !== false,
    ...((s as any).canonicalSpecies ? { canonicalSpecies: (s as any).canonicalSpecies } : {}),
    caution: String((s as any).caution || '').trim() || undefined,
    habitats,
    regions,
    summary: s.summary || s.feedingProfile?.notes || '',
    keyTraits: normalizeStringArray(s.keyTraits, 8),
    behaviorTags: normalizeStringArray((s as any).behaviorTags, 8),
    structureTags: normalizeStringArray((s as any).structureTags, 10),
    activeTimes: normalizeStringArray((s as any).activeTimes || s.bestTimesOfDay, 6),
    depthPreference: normalizeDepthPreference((s as any).depthPreference),
    typicalSize: s.typicalSize,
    ...normalizeImageMeta(s),
    feedingProfile: s.feedingProfile
      ? {
          primaryPrey: uniqueStrings(s.feedingProfile.primaryPrey || []),
          prey: uniqueStrings(s.feedingProfile.prey || []),
          notes: s.feedingProfile.notes || '',
        }
      : undefined,
    bestBaits: normalizeStringArray(s.bestBaits, 12),
    bestTechniques: normalizeStringArray(s.bestTechniques, 12),
    bestSeasons: normalizeStringArray(s.bestSeasons, 8),
    bestTimesOfDay: normalizeStringArray(s.bestTimesOfDay, 8),
    tactics: {
      bestBaits: normalizeStringArray(s.bestBaits, 12),
      bestTechniques: normalizeStringArray(s.bestTechniques, 12),
      bestSeasons: normalizeStringArray(s.bestSeasons, 8),
      bestTimesOfDay: normalizeStringArray(s.bestTimesOfDay, 8),
    },
    beginnerTips: Array.isArray(s.beginnerTips) ? uniqueStrings(s.beginnerTips) : [],
    advancedTips: Array.isArray(s.advancedTips) ? uniqueStrings(s.advancedTips) : [],
    seoSourceKey: s.seoSourceKey,
    editorialSources: Array.isArray(s.editorialSources) ? s.editorialSources : [],
    confidence: s.confidence || 'editorial',
    createdAt: Number(s.createdAt ?? now),
    updatedAt: Number(s.updatedAt ?? now),
  };
};

const toRegionListFromSeed = (seed: FishIQSpeciesSeed[]): FishIQRegion[] => {
  const set = new Set<string>();
  seed.forEach((s) => {
    (s.regions || []).forEach((r) => {
      const label = String(r ?? '').trim();
      if (label) set.add(label);
    });
  });
  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      id: normalize(name).replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      name,
      country: '',
      stateOrProvince: undefined,
      summary: '',
      waterbodies: [],
    }));
};

const normalizeSpeciesRecord = (raw: any, fallback?: Partial<FishIQSpecies>): FishIQSpecies => {
  const id = String(raw?.id ?? fallback?.id ?? '').trim();
  const commonName = String(raw?.commonName ?? fallback?.commonName ?? id).trim();
  const now = Date.now();
  return {
    ...(fallback || {}),
    ...(raw || {}),
    id,
    slug: String(raw?.slug ?? fallback?.slug ?? slugFromId(id)).trim(),
    commonName,
    scientificName: raw?.scientificName ?? fallback?.scientificName,
    aliases: uniqueStrings(raw?.aliases ?? fallback?.aliases ?? []),
    family: raw?.family ?? fallback?.family,
    category: raw?.category ?? fallback?.category,
    aquaticKind: raw?.aquaticKind ?? fallback?.aquaticKind ?? 'finfish',
    waterRealms: normalizeStringArray(raw?.waterRealms ?? fallback?.waterRealms ?? [], 6) as any,
    harvestModes: normalizeStringArray(raw?.harvestModes ?? fallback?.harvestModes ?? [], 8) as any,
    edibleCommonly: Boolean(raw?.edibleCommonly ?? fallback?.edibleCommonly ?? false),
    fishiqEligible: raw?.fishiqEligible ?? fallback?.fishiqEligible ?? true,
    ...((raw?.canonicalSpecies ?? (fallback as any)?.canonicalSpecies)
      ? { canonicalSpecies: raw?.canonicalSpecies ?? (fallback as any)?.canonicalSpecies }
      : {}),
    caution: String(raw?.caution ?? fallback?.caution ?? '').trim() || undefined,
    habitats: dedupeHabitats(raw?.habitats ?? fallback?.habitats ?? []),
    regions: uniqueStrings(raw?.regions ?? fallback?.regions ?? []),
    summary: String(raw?.summary ?? fallback?.summary ?? '').trim(),
    keyTraits: normalizeStringArray(raw?.keyTraits ?? fallback?.keyTraits ?? [], 8),
    behaviorTags: normalizeStringArray(raw?.behaviorTags ?? fallback?.behaviorTags ?? [], 8),
    structureTags: normalizeStringArray(raw?.structureTags ?? fallback?.structureTags ?? [], 10),
    activeTimes: normalizeStringArray(raw?.activeTimes ?? fallback?.activeTimes ?? raw?.bestTimesOfDay ?? fallback?.bestTimesOfDay ?? [], 6),
    depthPreference: normalizeDepthPreference(raw?.depthPreference ?? fallback?.depthPreference),
    typicalSize: raw?.typicalSize ?? fallback?.typicalSize,
    ...normalizeImageMeta(raw, fallback),
    feedingProfile:
      raw?.feedingProfile || fallback?.feedingProfile
        ? {
            primaryPrey: uniqueStrings(raw?.feedingProfile?.primaryPrey ?? fallback?.feedingProfile?.primaryPrey ?? []),
            prey: uniqueStrings(raw?.feedingProfile?.prey ?? fallback?.feedingProfile?.prey ?? []),
            notes: String(raw?.feedingProfile?.notes ?? fallback?.feedingProfile?.notes ?? '').trim(),
          }
        : undefined,
    bestBaits: normalizeStringArray(raw?.bestBaits ?? fallback?.bestBaits ?? [], 12),
    bestTechniques: normalizeStringArray(raw?.bestTechniques ?? fallback?.bestTechniques ?? [], 12),
    bestSeasons: normalizeStringArray(raw?.bestSeasons ?? fallback?.bestSeasons ?? [], 8),
    bestTimesOfDay: normalizeStringArray(raw?.bestTimesOfDay ?? fallback?.bestTimesOfDay ?? [], 8),
    tactics:
      raw?.tactics || fallback?.tactics
        ? {
            bestBaits: raw?.tactics?.bestBaits ?? raw?.bestBaits ?? fallback?.tactics?.bestBaits ?? fallback?.bestBaits ?? [],
            bestTechniques: raw?.tactics?.bestTechniques ?? raw?.bestTechniques ?? fallback?.tactics?.bestTechniques ?? fallback?.bestTechniques ?? [],
            bestSeasons: raw?.tactics?.bestSeasons ?? raw?.bestSeasons ?? fallback?.tactics?.bestSeasons ?? fallback?.bestSeasons ?? [],
            bestTimesOfDay: raw?.tactics?.bestTimesOfDay ?? raw?.bestTimesOfDay ?? fallback?.tactics?.bestTimesOfDay ?? fallback?.bestTimesOfDay ?? [],
          }
        : undefined,
    beginnerTips: uniqueStrings(raw?.beginnerTips ?? fallback?.beginnerTips ?? []),
    advancedTips: uniqueStrings(raw?.advancedTips ?? fallback?.advancedTips ?? []),
    seoSourceKey: raw?.seoSourceKey ?? fallback?.seoSourceKey,
    editorialSources: Array.isArray(raw?.editorialSources) ? raw.editorialSources : Array.isArray(fallback?.editorialSources) ? fallback.editorialSources : [],
    confidence: raw?.confidence ?? fallback?.confidence ?? 'editorial',
    createdAt: Number(raw?.createdAt ?? fallback?.createdAt ?? now),
    updatedAt: Number(raw?.updatedAt ?? fallback?.updatedAt ?? now),
  };
};

const mergeSpecies = (seedSpecies: FishIQSpecies[], firestoreSpecies: any[]): FishIQSpecies[] => {
  const seedById: Record<string, FishIQSpecies> = {};
  for (const s of seedSpecies) seedById[String(s.id)] = s;
  const fsById: Record<string, any> = {};
  for (const d of firestoreSpecies) {
    const id = String(d?.id ?? '').trim();
    if (!id) continue;
    fsById[id] = d;
  }
  const out: FishIQSpecies[] = [];
  const allIds = new Set<string>([...Object.keys(seedById), ...Object.keys(fsById)]);
  for (const id of Array.from(allIds)) {
    const seed = seedById[id] ?? null;
    const fs = fsById[id] ?? null;
    const merged = normalizeSpeciesRecord(fs || seed || {}, seed || undefined);
    const count = getObservationCount(fs);
    const source: FishIQMeta['source'] = seed && fs ? 'hybrid' : fs ? 'firestore' : 'seed';
    out.push(attachMeta(merged, { source, observationCount: count }));
  }
  out.sort((a: any, b: any) => {
    const aTier = String(a?.__fishiq?.confidenceTier ?? 'general');
    const bTier = String(b?.__fishiq?.confidenceTier ?? 'general');
    const tierRank = (tier: string) => (tier === 'local' ? 2 : tier === 'limited' ? 1 : 0);
    const tr = tierRank(bTier) - tierRank(aTier);
    if (tr !== 0) return tr;
    const ac = Number(a?.__fishiq?.observationCount ?? 0);
    const bc = Number(b?.__fishiq?.observationCount ?? 0);
    if (bc !== ac) return bc - ac;
    return String(a?.commonName ?? '').localeCompare(String(b?.commonName ?? ''));
  });
  return out;
};

const readFirestoreSpecies = async (): Promise<any[]> => {
  if (!FEATURES.FISHIQ_FIRESTORE_SPECIES_OVERLAY) return [];

  try {
    const snap = await getDocs(collection(db, 'fishIQSpecies'));
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
  } catch (e) {
    console.warn('[FishIQ] Firestore species read failed (seed fallback)', e);
    return [];
  }
};

const habitatScoreForBias = (habitats: FishHabitatType[], bias: FishHabitatType | null): number => {
  if (!bias || !habitats?.length) return 0;
  if (habitats.includes(bias)) return 1.0;
  if (bias === 'creek' && habitats.includes('river')) return 0.86;
  if (bias === 'river' && habitats.includes('creek')) return 0.82;
  if (bias === 'inshore' && (habitats.includes('bay') || habitats.includes('estuary') || habitats.includes('ocean'))) return 0.84;
  if (bias === 'bay' && (habitats.includes('inshore') || habitats.includes('estuary'))) return 0.82;
  if (bias === 'estuary' && (habitats.includes('bay') || habitats.includes('inshore'))) return 0.82;
  if (bias === 'lake' && habitats.includes('reservoir')) return 0.74;
  if (bias === 'reservoir' && habitats.includes('lake')) return 0.74;
  if (bias === 'ocean' && habitats.includes('inshore')) return 0.65;
  if (bias === 'inshore' && habitats.includes('ocean')) return 0.65;
  return 0;
};

export const withWaterTypeBias = (species: FishIQSpecies[], waterTypeBias?: WaterTypeBias): FishIQSpecies[] => {
  const normalizedBias = normalizeWaterTypeBias(waterTypeBias);
  if (!normalizedBias) return species;

  // Trust guard: when FishIQ has a water read, incompatible salt/fresh species should not
  // appear as normal area-read candidates. This keeps inland reads freshwater-safe and
  // coastal reads saltwater-safe while still allowing shared/user memory elsewhere.
  const guarded = species.filter((item: any) => speciesMatchesWaterTruth(item, normalizedBias));

  return [...guarded].sort((a: any, b: any) => {
    const aScore = habitatScoreForBias(Array.isArray(a?.habitats) ? a.habitats : [], normalizedBias);
    const bScore = habitatScoreForBias(Array.isArray(b?.habitats) ? b.habitats : [], normalizedBias);
    if (bScore !== aScore) return bScore - aScore;

    const aObs = Number(a?.__fishiq?.observationCount ?? 0);
    const bObs = Number(b?.__fishiq?.observationCount ?? 0);
    if (bObs !== aObs) return bObs - aObs;

    return String(a?.commonName ?? '').localeCompare(String(b?.commonName ?? ''));
  }).map((item: any) => {
    const score = habitatScoreForBias(Array.isArray(item?.habitats) ? item.habitats : [], normalizedBias);
    return {
      ...item,
      __waterBias: {
        requestedType: normalizedBias,
        fitScore: score,
        label:
          score >= 1
            ? `Strong ${normalizedBias} match`
            : score >= 0.8
              ? `Good ${normalizedBias} fit`
              : score >= 0.65
                ? `Possible ${normalizedBias} fit`
                : 'Exploratory water fit',
      },
    } as FishIQSpecies;
  });
};

export const listSpecies = async (): Promise<FishIQSpecies[]> => {
  if (cachedSpecies) return cachedSpecies;
  const seedList = (FISHIQ_SPECIES_SEED as FishIQSpeciesSeed[]).map(toSpecies);
  const fsList = await readFirestoreSpecies();
  cachedSpecies = mergeSpecies(seedList, fsList);
  return cachedSpecies;
};

export const listSpeciesForWaterType = async (waterTypeBias?: WaterTypeBias): Promise<FishIQSpecies[]> => {
  const all = await listSpecies();
  return withWaterTypeBias(all, waterTypeBias);
};

export const getSpeciesById = async (id: string): Promise<FishIQSpecies | null> => {
  const all = await listSpecies();
  return all.find((s) => s.id === id) || null;
};

export const searchSpeciesByName = async (
  term: string,
  opts?: { waterTypeBias?: WaterTypeBias },
): Promise<FishIQSpecies[]> => {
  const all = await listSpecies();
  const q = normalize(term);

  // Species search is intentionally identity-only.
  // Do not match habitat, region, summary, prey, bait, techniques, behavior,
  // or other FishIQ guidance fields. Those made searches like "shrimp" return
  // dozens of species that merely eat shrimp instead of the Shrimp card.
  const base = !q
    ? all
    : all.filter((s: any) => {
        const identityFields = [
          s?.id,
          s?.slug,
          s?.key,
          s?.speciesKey,
          s?.canonicalSpeciesId,
          s?.canonicalSpecies?.canonicalId,
          s?.commonName,
          s?.name,
          s?.scientificName,
          s?.taxonomy?.scientificName,
          s?.family,
          ...(Array.isArray(s?.aliases) ? s.aliases : []),
          ...(Array.isArray(s?.legacyIds) ? s.legacyIds : []),
          ...(Array.isArray(s?.canonicalSpecies?.legacyIds)
            ? s.canonicalSpecies.legacyIds
            : []),
        ];

        return identityFields.some((value) => normalize(value).includes(q));
      });

  return withWaterTypeBias(base, opts?.waterTypeBias);
};

export const listRegions = async (): Promise<FishIQRegion[]> => {
  if (cachedRegions) return cachedRegions;
  cachedRegions = toRegionListFromSeed(FISHIQ_SPECIES_SEED as FishIQSpeciesSeed[]);
  return cachedRegions;
};

export const getRegionById = async (id: string): Promise<FishIQRegion | null> => {
  const all = await listRegions();
  return all.find((r) => r.id === id) || null;
};

export const searchRegionsByName = async (term: string): Promise<FishIQRegion[]> => {
  const all = await listRegions();
  const q = normalize(term);
  if (!q) return all;
  return all.filter((r) => normalize(r.name).includes(q));
};

const LS_KEY = 'fishiq_saved_v1';

type SavedPayload = { speciesIds: string[]; regionIds: string[] };

const readSaved = (): SavedPayload => {
  try {
    if (typeof window === 'undefined') return { speciesIds: [], regionIds: [] };
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { speciesIds: [], regionIds: [] };
    const parsed = JSON.parse(raw) as Partial<SavedPayload>;
    return {
      speciesIds: Array.isArray(parsed.speciesIds) ? parsed.speciesIds : [],
      regionIds: Array.isArray(parsed.regionIds) ? parsed.regionIds : [],
    };
  } catch {
    return { speciesIds: [], regionIds: [] };
  }
};

const writeSaved = (payload: SavedPayload) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LS_KEY, JSON.stringify(payload));
};

export const getSavedIds = (): SavedPayload => readSaved();

export const toggleSavedSpecies = (speciesId: string): SavedPayload => {
  const payload = readSaved();
  const exists = payload.speciesIds.includes(speciesId);
  payload.speciesIds = exists ? payload.speciesIds.filter((id) => id !== speciesId) : [...payload.speciesIds, speciesId];
  writeSaved(payload);
  return payload;
};

export const toggleSavedRegion = (regionId: string): SavedPayload => {
  const payload = readSaved();
  const exists = payload.regionIds.includes(regionId);
  payload.regionIds = exists ? payload.regionIds.filter((id) => id !== regionId) : [...payload.regionIds, regionId];
  writeSaved(payload);
  return payload;
};

export const __resetFishIQCache = () => {
  cachedSpecies = null;
  cachedRegions = null;
};

export const prettyFishSpeciesNameFromId = (id: string) => {
  const raw = String(id || '').trim();
  if (!raw) return '';
  return titleCaseWords(raw.replace(/[_-]+/g, ' '));
};
